import { useMemo } from "react";

import { useSidebarMenu } from "byzantine";

import { useCurrentUser } from "../../../components/contexts/CurrentUserContext";

import { useAccountsMenuItem } from "./builders/accounts";
import { useTransferMenuItem } from "./builders/transfers";
import { useManagePaymentsMenuItem } from "./builders/managePayments";
import { useNAFOptions } from "./builders/naf";
import { useTopLevelNAFMenuItem } from "./builders/topLevelNaf";
import { useDefaultMenuItem } from "./builders/defaultItem";

import type { LegacyNafUrl } from "types";
import type { SidebarRootItem } from "./sidebar.types";

/**
 * Returns true if the menu item should be shown for the current user.
 * Currently only filters "reporting" for non-business users.
 */
const shouldShowMenuItem = (
  menuItem: API.SidebarMenuItem_Resolved,
  currentUser: ReturnType<typeof useCurrentUser>["currentUser"]
): boolean => {
  if (menuItem.key === "reporting" && !currentUser?.isBusiness()) {
    return false;
  }
  return true;
};

/**
 * Container hook that compiles sidebar menu items.
 *
 * Data flow:
 *   1. Fetch resolved menu items from byzantine (feature-flag filtered)
 *   2. Filter by user type (e.g. reporting -> business only)
 *   3. Dispatch each item to its builder hook to produce SidebarRootItem[]
 *
 * Each builder hook internalizes its own data dependencies
 * (localization, features, NAF items, etc.) so the orchestrator
 * only manages the dispatch logic.
 */
export const useSidebarMenuContainer = ({
  legacyNafUrls,
}: {
  legacyNafUrls?: LegacyNafUrl[];
}) => {
  const { currentUser } = useCurrentUser();
  const sidebarMenu = useSidebarMenu();

  const buildAccountsMenuItem = useAccountsMenuItem();
  const buildTransferMenuItem = useTransferMenuItem();
  const buildManagePaymentsMenuItem = useManagePaymentsMenuItem();
  const nafOptions = useNAFOptions(legacyNafUrls);
  const buildTopLevelNAFMenuItem = useTopLevelNAFMenuItem();
  const buildDefaultMenuItem = useDefaultMenuItem();

  return useMemo((): SidebarRootItem[] => {
    if (currentUser === null) {
      return [];
    }

    return sidebarMenu
      .filter((menuItem) => shouldShowMenuItem(menuItem, currentUser))
      .map((menuItem): SidebarRootItem | null => {
        switch (menuItem.key) {
          case "accounts":
            return buildAccountsMenuItem(menuItem);

          case "pay-and-transfer":
            return buildTransferMenuItem(menuItem);

          case "manage-payments":
            return buildManagePaymentsMenuItem(menuItem);

          default: {
            if (menuItem.nafClientId) {
              return buildTopLevelNAFMenuItem(menuItem);
            }
            return buildDefaultMenuItem(menuItem, nafOptions);
          }
        }
      })
      .filter((item): item is SidebarRootItem => {
        // byzantine keeps NAF/AOIOB sections alive even when their standard
        // links are all hidden, so they can be populated here with the actual
        // NAF apps / AOIOB entry. By this point `menuOptions` is the fully
        // compiled list (standard links + NAF apps + AOIOB), so it — not the
        // `naf`/`aoiob` config flags — is the source of truth: a section can be
        // flagged `naf`/`aoiob` yet produce no entries (e.g. the user has no NAF
        // apps, or the AOIOB feature is off). Drop a section only when it has
        // nothing to render: no custom options, no compiled options, and no
        // direct-link `route` (header-only sections render as a direct link).
        const hasContent =
          !!item &&
          (!!item.customMenuOptions ||
            Boolean(item.menuOptions?.length) ||
            !!item.route);
        return hasContent;
      });
  }, [
    currentUser,
    sidebarMenu,
    buildAccountsMenuItem,
    buildTransferMenuItem,
    buildManagePaymentsMenuItem,
    nafOptions,
    buildTopLevelNAFMenuItem,
    buildDefaultMenuItem,
  ]);
};
